import { use, useCallback, useEffect } from 'react';

import { SiteContext } from 'src/context/site-context';
import { userHasConsentedToAllOptionalCookieCategories } from 'src/cookies/consent-cookie';
import { useUpdateDocumentClass } from 'src/custom-hooks/use-document-class-while-rendered';
import usePuzzelLiveChatFeatureFlag from 'src/custom-hooks/use-puzzel-live-chat-feature-flag';
import { BuildType } from 'src/enums/build/build-type';
import abby, { ABBY_CHAT_ICON_CLASS } from 'src/helpers/abby';
import { isAbbySupportedByBuildType } from 'src/helpers/live-chat-helper';
import newPuzzel from 'src/helpers/newPuzzel';
import puzzel, { PUZZEL_CHAT_CLASS } from 'src/helpers/puzzel';
import tawk from 'src/helpers/tawk';

export const shouldUseOldPuzzel = (buildType: BuildType) => buildType === BuildType.DAVID_LLOYD_UK;
export const useLiveChat = ({
  siteId,
  actionTawkHandlerId,
  noTawkInit,
  liveChatEnabled,
  abbyChatEnabled,
}: {
  siteId?: string;
  liveChatEnabled?: boolean;
  abbyChatEnabled?: boolean;
  actionTawkHandlerId?: ({ tawkHandlerId }: { tawkHandlerId?: number | null }) => void;
  noTawkInit?: boolean;
} = {}) => {
  const { buildType, siteId: defaultSiteId } = use(SiteContext);

  const usePuzzelForLiveChat = usePuzzelLiveChatFeatureFlag();
  const puzzelFeatureFlagHasLoaded = usePuzzelForLiveChat !== null;
  const updateShowPuzzelChat = useUpdateDocumentClass(PUZZEL_CHAT_CLASS);
  const updateShowAbbyChat = useUpdateDocumentClass(ABBY_CHAT_ICON_CLASS);
  const useAbbyChat = !!abbyChatEnabled && !liveChatEnabled;
  const isAbbySupported = isAbbySupportedByBuildType(buildType);

  const setPuzzelWidgetVisibility = useCallback(() => {
    updateShowAbbyChat(useAbbyChat && isAbbySupported);
    updateShowPuzzelChat(!useAbbyChat);
    if (liveChatEnabled) {
      if (shouldUseOldPuzzel(buildType)) {
        puzzel.init();
      } else {
        newPuzzel.init();
      }
    }
  }, [
    liveChatEnabled,
    updateShowAbbyChat,
    useAbbyChat,
    isAbbySupported,
    updateShowPuzzelChat,
    buildType,
  ]);

  const setTawkWidgetVisibility = useCallback(
    (status: string) => {
      if (!liveChatEnabled || abbyChatEnabled || (status && status === 'offline')) {
        tawk.hideWidget();
      } else {
        if (!noTawkInit) {
          tawk.init(buildType, siteId ?? defaultSiteId);
        }
        tawk.showWidget();
      }
    },
    [liveChatEnabled, abbyChatEnabled, noTawkInit, buildType, siteId, defaultSiteId]
  );

  const setAbbyChatVisibility = useCallback(() => {
    updateShowAbbyChat(useAbbyChat && isAbbySupported);
    updateShowPuzzelChat(!useAbbyChat);
    tawk.hideWidget();

    if (useAbbyChat) {
      abby.init(buildType);
    } else {
      abby.remove();
    }
  }, [updateShowAbbyChat, useAbbyChat, isAbbySupported, updateShowPuzzelChat, buildType]);

  useEffect(() => {
    let tawkHandlerId: number | undefined;

    if (puzzelFeatureFlagHasLoaded && !usePuzzelForLiveChat && !abbyChatEnabled) {
      tawkHandlerId = tawk.addStatusChangeHandler((status) =>
        setTawkWidgetVisibility(status as string)
      );
      if (actionTawkHandlerId) {
        actionTawkHandlerId({ tawkHandlerId });
      }
    }

    return () => {
      if (tawkHandlerId) {
        tawk.removeStatusChangeHandler(tawkHandlerId);
      }
    };
  }, [
    puzzelFeatureFlagHasLoaded,
    setTawkWidgetVisibility,
    usePuzzelForLiveChat,
    actionTawkHandlerId,
    abbyChatEnabled,
  ]);

  useEffect(() => {
    if (!userHasConsentedToAllOptionalCookieCategories()) return;
    if (puzzelFeatureFlagHasLoaded && !abbyChatEnabled) {
      if (usePuzzelForLiveChat) {
        setPuzzelWidgetVisibility();
      } else {
        setTawkWidgetVisibility(tawk.getStatus(siteId ?? defaultSiteId) as string);
      }
    }
    if (isAbbySupported && abbyChatEnabled) {
      setAbbyChatVisibility();
    }
  }, [
    siteId,
    puzzelFeatureFlagHasLoaded,
    usePuzzelForLiveChat,
    setPuzzelWidgetVisibility,
    setTawkWidgetVisibility,
    defaultSiteId,
    setAbbyChatVisibility,
    abbyChatEnabled,
    isAbbySupported,
  ]);
};
